import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Plus, Edit2, Trash2, Loader2, MapPin, Truck, Eye, Printer, Building2, Navigation2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import Pagination from '../../components/ui/pagination';
import VehicleTrackModal from '../../components/VehicleTrackModal';

const IN_PROGRESS = new Set(['ACTIVE', 'PARKING', 'LOADING', 'IN_TRANSIT', 'UNLOADING']);

const TripList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [trips, setTrips] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [trackingTrip, setTrackingTrip] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  // Detect super admin and fetch companies
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'super_admin') {
          setIsSuperAdmin(true);
          fetch(`${backendUrl}/api/companies`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : [])
            .then(setCompanies);
        }
      } catch {}
    }
  }, [backendUrl]);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (companyFilter !== 'all') params.append('company_id', companyFilter);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const res = await fetch(`${backendUrl}/api/trips?${params}`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setTrips(d.items || []); setTotal(d.total || 0); }
    } catch {
      toast({ title: "Error", description: "Failed to load trips", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, statusFilter, companyFilter, search, page, pageSize, toast]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);
  useEffect(() => { setPage(1); }, [statusFilter, companyFilter]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Deleted", description: "Trip removed" });
      fetchTrips();
    } catch {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    } finally {
      setDeleteTarget(null);
    }
  };

  const companyName = (company_id) => {
    const c = companies.find(x => x.id === company_id);
    return c ? (c.name || c.company_name) : null;
  };

  const statusBadge = (s) => {
    const map = {
      ACTIVE: 'bg-accent-green/15 text-green-700',
      COMPLETED: 'bg-blue-100 text-blue-700',
      CANCELLED: 'bg-accent-red/15 text-accent-red',
    };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="trip-list-title" className="text-3xl font-bold text-text-dark font-manrope">Trip Management</h1>
          <p className="text-text-secondary font-ibm mt-1">Manage company trips, vehicles and dockets</p>
        </div>
        <Button data-testid="add-trip-btn" onClick={() => navigate('/fleet/trips/add')}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg">
          <Plus className="w-5 h-5 mr-2" />New Trip
        </Button>
      </div>

      <Card className="bg-primary-blue text-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="text-xl font-semibold font-manrope mr-2">All Trips ({total})</h3>
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <Input data-testid="trip-search-input" placeholder="Search trip no, vehicle, route..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 font-ibm" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="trip-status-filter" className="w-44 bg-white/10 border-white/20 text-white font-ibm">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {isSuperAdmin && companies.length > 0 && (
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="trip-company-filter" className="w-52 bg-white/10 border-white/20 text-white font-ibm">
                <Building2 className="w-4 h-4 mr-1 opacity-70" />
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
          </div>
        ) : trips.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Truck className="w-12 h-12 text-text-secondary/40" />
            <p className="text-text-secondary font-ibm">No trips found</p>
            <Button onClick={() => navigate('/fleet/trips/add')} className="bg-accent-blue text-white font-ibm">
              <Plus className="w-4 h-4 mr-2" />Create First Trip
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="trips-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'Trip No.', 'Date', 'Status', ...(isSuperAdmin ? ['Company'] : []), 'Vehicle', 'Driver', 'Route', 'Advance', 'Created By', 'Dockets', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {trips.map((t, idx) => (
                  <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono font-semibold text-accent-blue">{t.trip_number}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm whitespace-nowrap">{t.trip_date}</td>
                    <td className="px-4 py-3">
                      <Badge data-testid={`trip-status-${t.id}`} className={`font-ibm border-0 text-xs ${statusBadge(t.status)}`}>
                        {t.status}
                      </Badge>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-text-secondary/60 shrink-0" />
                          <span className="text-xs text-text-secondary font-ibm">{companyName(t.company_id) || '—'}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-text-dark font-ibm">{t.vehicle_name || '—'}</div>
                      <div className="text-xs text-text-secondary font-mono">{t.vehicle_registration}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm whitespace-nowrap">{t.driver_name || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-text-secondary font-ibm">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="whitespace-nowrap">{t.from_location || '—'} → {t.to_location || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{(t.advance || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{t.created_by_name || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge className="bg-gray-100 text-gray-700 border-0 text-xs font-ibm">{t.docket_count}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {IN_PROGRESS.has(t.status) && (
                          <button
                            data-testid={`track-trip-${t.id}`}
                            onClick={() => setTrackingTrip(t)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 rounded-lg"
                            title="Track Vehicle"
                          >
                            <Navigation2 className="w-4 h-4 text-blue-500" />
                          </button>
                        )}
                        <button data-testid={`view-trip-${t.id}`} onClick={() => navigate(`/fleet/trips/view/${t.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="View">
                          <Eye className="w-4 h-4 text-text-secondary" />
                        </button>
                        <button data-testid={`print-trip-${t.id}`} onClick={() => navigate(`/fleet/trips/print/${t.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-green-50 rounded-lg" title="Print">
                          <Printer className="w-4 h-4 text-green-600" />
                        </button>
                        <button data-testid={`edit-trip-${t.id}`} onClick={() => navigate(`/fleet/trips/edit/${t.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="Edit">
                          <Edit2 className="w-4 h-4 text-accent-blue" />
                        </button>
                        <button data-testid={`delete-trip-${t.id}`} onClick={() => setDeleteTarget(t)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && trips.length > 0 && (
          <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        )}
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trip</AlertDialogTitle>
            <AlertDialogDescription>Delete trip "{deleteTarget?.trip_number}"? All associated docket assignments will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-accent-red hover:bg-accent-red/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {trackingTrip && (
        <VehicleTrackModal
          trip={trackingTrip}
          onClose={() => setTrackingTrip(null)}
        />
      )}
    </div>
  );
};

export default TripList;

